import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import announcementsApi, { Announcement, AnnouncementRequest, AnnouncementTag } from '../../../services/announcementsApi.ts';
import './AdminUpdatesPage.css';

const COLORS = ['#000000', '#ffffff', '#2962ff', '#f57c00', '#c62828', '#2e7d32', '#6a1b9a', '#00838f'];
const MAX_VIDEO_SIZE = 500 * 1024 * 1024; // 500MB

const AdminUpdatesPage: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);

  const [title, setTitle] = useState('');
  const [tag, setTag] = useState<AnnouncementTag>('INFO');
  const [isPinned, setIsPinned] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    setLoading(true);
    announcementsApi.getAllAnnouncements()
      .then(setAnnouncements)
      .catch(() => toast.error('Failed to load announcements'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setTitle('');
    setTag('INFO');
    setIsPinned(false);
    setIsActive(true);
    setVideoUrl('');
    setUploadProgress(null);
    setEditingId(null);
    if (editorRef.current) editorRef.current.innerHTML = '';
    if (videoInputRef.current) videoInputRef.current.value = '';
  };

  const openCreate = () => { resetForm(); setShowForm(true); };

  const openEdit = (item: Announcement) => {
    setEditingId(item.id);
    setTitle(item.title);
    setTag(item.tag);
    setIsPinned(item.isPinned);
    setIsActive(item.isActive);
    setVideoUrl(item.videoUrl || '');
    setShowForm(true);
    setTimeout(() => {
      if (editorRef.current) editorRef.current.innerHTML = item.content;
    }, 50);
  };

  const closeForm = () => { setShowForm(false); resetForm(); };

  const exec = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const handleColorClick = (color: string) => exec('foreColor', color);
  const handleLink = () => { const url = prompt('Enter URL:'); if (url) exec('createLink', url); };

  const handleVideoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_VIDEO_SIZE) {
      toast.error('Video must be under 500MB');
      if (videoInputRef.current) videoInputRef.current.value = '';
      return;
    }

    if (!file.type.startsWith('video/')) {
      toast.error('Only video files are allowed');
      if (videoInputRef.current) videoInputRef.current.value = '';
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    try {
      const url = await announcementsApi.uploadVideo(file, (pct) => setUploadProgress(pct));
      setVideoUrl(url);
      toast.success('Video uploaded successfully');
    } catch {
      toast.error('Failed to upload video');
      if (videoInputRef.current) videoInputRef.current.value = '';
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const removeVideo = () => {
    setVideoUrl('');
    if (videoInputRef.current) videoInputRef.current.value = '';
  };

  const handleSave = async () => {
    const content = editorRef.current?.innerHTML?.trim() ?? '';
    if (!title.trim()) { toast.error('Title is required'); return; }
    if (!content || content === '<br>') { toast.error('Content is required'); return; }

    const request: AnnouncementRequest = {
      title: title.trim(), content, tag, isPinned, isActive,
      videoUrl: videoUrl || undefined,
    };
    setSaving(true);
    try {
      if (editingId !== null) {
        await announcementsApi.update(editingId, request);
        toast.success('Announcement updated');
      } else {
        await announcementsApi.create(request);
        toast.success('Announcement created');
      }
      closeForm();
      load();
    } catch {
      toast.error('Failed to save announcement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this announcement? The attached video will also be deleted.')) return;
    setDeleting(id);
    try {
      await announcementsApi.delete(id);
      toast.success('Deleted');
      setAnnouncements(prev => prev.filter(a => a.id !== id));
    } catch {
      toast.error('Failed to delete');
    } finally {
      setDeleting(null);
    }
  };

  const tagColors: Record<string, string> = { INFO: '#2962ff', IMPORTANT: '#f57c00', WARNING: '#c62828' };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });

  return (
    <div className="aup-page">
      <div className="aup-top-bar">
        <div>
          <h2 className="aup-heading">Updates & Announcements</h2>
          <p className="aup-sub">Manage updates visible to premium users</p>
        </div>
        <button className="aup-btn-primary" onClick={openCreate}>+ New Update</button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="aup-form-card">
          <h3 className="aup-form-title">{editingId ? 'Edit Announcement' : 'New Announcement'}</h3>

          <div className="aup-field">
            <label className="aup-label">Title</label>
            <input className="aup-input" placeholder="Enter title…" value={title} onChange={e => setTitle(e.target.value)} />
          </div>

          <div className="aup-row">
            <div className="aup-field">
              <label className="aup-label">Tag</label>
              <select className="aup-select" value={tag} onChange={e => setTag(e.target.value as AnnouncementTag)}>
                <option value="INFO">Info</option>
                <option value="IMPORTANT">Important</option>
                <option value="WARNING">Warning</option>
              </select>
            </div>
            <div className="aup-field aup-checkbox-field">
              <label className="aup-checkbox-label">
                <input type="checkbox" checked={isPinned} onChange={e => setIsPinned(e.target.checked)} />
                Pin to top
              </label>
              <label className="aup-checkbox-label">
                <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
                Active (visible)
              </label>
            </div>
          </div>

          <div className="aup-field">
            <label className="aup-label">Content</label>
            <div className="aup-editor-wrapper">
              <div className="aup-toolbar">
                <button className="aup-tool" title="Bold" onMouseDown={e => { e.preventDefault(); exec('bold'); }}><b>B</b></button>
                <button className="aup-tool" title="Italic" onMouseDown={e => { e.preventDefault(); exec('italic'); }}><i>I</i></button>
                <button className="aup-tool" title="Underline" onMouseDown={e => { e.preventDefault(); exec('underline'); }}><u>U</u></button>
                <span className="aup-toolbar-sep" />
                <button className="aup-tool" title="Align Left" onMouseDown={e => { e.preventDefault(); exec('justifyLeft'); }}>&#8676;</button>
                <button className="aup-tool" title="Align Center" onMouseDown={e => { e.preventDefault(); exec('justifyCenter'); }}>&#8677;</button>
                <button className="aup-tool" title="Align Right" onMouseDown={e => { e.preventDefault(); exec('justifyRight'); }}>&#8678;</button>
                <span className="aup-toolbar-sep" />
                {COLORS.map(c => (
                  <button key={c} className="aup-color-btn" title={c}
                    style={{ background: c, border: c === '#ffffff' ? '1.5px solid #ccc' : 'none' }}
                    onMouseDown={e => { e.preventDefault(); handleColorClick(c); }} />
                ))}
                <span className="aup-toolbar-sep" />
                <button className="aup-tool" title="Insert Link" onMouseDown={e => { e.preventDefault(); handleLink(); }}>🔗</button>
                <button className="aup-tool" title="Remove Formatting" onMouseDown={e => { e.preventDefault(); exec('removeFormat'); }}>✕</button>
              </div>
              <div ref={editorRef} className="aup-editor" contentEditable suppressContentEditableWarning data-placeholder="Write your update here…" />
            </div>
          </div>

          {/* Video Upload */}
          <div className="aup-field">
            <label className="aup-label">Video (optional · max 500MB)</label>

            {!videoUrl && !uploading && (
              <div className="aup-video-drop" onClick={() => videoInputRef.current?.click()}>
                <div className="aup-video-drop-icon">🎬</div>
                <div className="aup-video-drop-text">Click to select a video</div>
                <div className="aup-video-drop-hint">MP4, MOV, AVI · Max 500MB</div>
                <input ref={videoInputRef} type="file" accept="video/*" onChange={handleVideoSelect} hidden />
              </div>
            )}

            {uploading && (
              <div className="aup-upload-progress-wrap">
                <div className="aup-upload-label">Uploading… {uploadProgress}%</div>
                <div className="aup-progress-bar">
                  <div className="aup-progress-fill" style={{ width: `${uploadProgress}%` }} />
                </div>
              </div>
            )}

            {videoUrl && !uploading && (
              <div className="aup-video-preview">
                <video src={videoUrl} controls className="aup-video-player" />
                <button className="aup-remove-video" onClick={removeVideo}>✕ Remove Video</button>
              </div>
            )}
          </div>

          <div className="aup-form-actions">
            <button className="aup-btn-ghost" onClick={closeForm}>Cancel</button>
            <button className="aup-btn-primary" onClick={handleSave} disabled={saving || uploading}>
              {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Publish'}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="aup-loading">Loading…</div>
      ) : announcements.length === 0 ? (
        <div className="aup-empty">No announcements yet. Create one above.</div>
      ) : (
        <div className="aup-list">
          {announcements.map(item => (
            <div key={item.id} className={`aup-item ${!item.isActive ? 'aup-inactive' : ''}`}>
              <div className="aup-item-left">
                <div className="aup-item-tag-row">
                  <span className="aup-item-tag" style={{ color: tagColors[item.tag] }}>{item.tag}</span>
                  {item.isPinned && <span className="aup-item-pin">📌</span>}
                  {item.videoUrl && <span className="aup-item-video-badge">🎬 Video</span>}
                  {!item.isActive && <span className="aup-item-hidden">Hidden</span>}
                </div>
                <div className="aup-item-title">{item.title}</div>
                <div className="aup-item-date">{formatDate(item.createdAt)}</div>
              </div>
              <div className="aup-item-actions">
                <button className="aup-action-btn" onClick={() => openEdit(item)}>Edit</button>
                <button className="aup-action-btn danger" onClick={() => handleDelete(item.id)} disabled={deleting === item.id}>
                  {deleting === item.id ? '…' : 'Delete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminUpdatesPage;
